import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { Toaster } from 'react-hot-toast';
import { Header, ProtectedRoute } from './components';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useStatusPreloader } from './hooks/useStatusPreloader';
import { trpc } from './lib/trpc';
import { HomePage } from './pages/HomePage';
import { InvoicesPage } from './pages/InvoicesPage';
import { InvoiceEditPage } from './pages/InvoiceEditPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { SupplierEditPage } from './pages/SupplierEditPage';
import { SupplierCreatePage } from './pages/SupplierCreatePage';
import { AuthPage } from './pages/AuthPage';
import { AdminPage } from './pages/AdminPage';

import './index.css';

function AppContent() {
  const { isAuthenticated } = useAuth();
  
  // Preload statuses for better performance
  useStatusPreloader();

  return (
    <div className="min-h-screen bg-background-secondary">
      {isAuthenticated && <Header />}
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/" element={
          <ProtectedRoute>
            <main className="container mx-auto px-4 py-8">
              <HomePage />
            </main>
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute>
            <main className="container mx-auto px-4 py-8">
              <InvoicesPage />
            </main>
          </ProtectedRoute>
        } />
        <Route path="/invoices/:id/edit" element={
          <ProtectedRoute>
            <InvoiceEditPage />
          </ProtectedRoute>
        } />
        <Route path="/suppliers" element={
          <ProtectedRoute>
            <main className="container mx-auto px-4 py-8">
              <SuppliersPage />
            </main>
          </ProtectedRoute>
        } />
        <Route path="/suppliers/new" element={
          <ProtectedRoute>
            <main className="container mx-auto px-4 py-8">
              <SupplierCreatePage />
            </main>
          </ProtectedRoute>
        } />
        <Route path="/suppliers/:id/edit" element={
          <ProtectedRoute>
            <main className="container mx-auto px-4 py-8">
              <SupplierEditPage />
            </main>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />

      </Routes>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: 'white',
            border: '1px solid #60a5fa',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            minWidth: '300px',
            padding: '12px 16px',
          },
          success: {
            style: {
              background: '#16a34a',
              color: 'white',
              border: '1px solid #22c55e',
            },
          },
          error: {
            style: {
              background: '#dc2626',
              color: 'white',
              border: '1px solid #ef4444',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  const [queryClient] = React.useState(() => new QueryClient());
  const [trpcClient] = React.useState(() => {
    const API_URL = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3001';
    
    return trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_URL}/trpc`,
          headers() {
            const token = localStorage.getItem('auth_token');
            return {
              authorization: token ? `Bearer ${token}` : '',
            };
          },
        }),
      ],
    });
  });

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

export default App; 